import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateTransportDriver,
  useDeleteTransportDriver,
  useGetTransportDrivers,
  useUpdateTransportDriver,
  type TransportDriver,
  type TransportDriverPayload,
} from 'api/transportDriver';
import { isSuccessful } from 'api/utils';
import { debounce } from 'lodash';
import { appToast } from 'utils/toast.utils';
import {
  EMPTY_DRAFT,
  EMPTY_ERRORS,
  isInvalid,
  MAX_LENGTHS,
} from '../components/Dialogs/DriversDatabase/DriversDatabaseTable.consts';
import {
  NEW_ROW_ID,
  type DriverDraft,
  type DriverRow,
} from '../components/Dialogs/DriversDatabase/DriversDatabaseTable.types';
import { DIALOGS, ERRORS } from '../TransportCloudTable.consts';

const toRow = ({
  id,
  fullName,
  documentNumber,
  phone,
}: TransportDriver): DriverRow => ({
  id: id ?? NEW_ROW_ID,
  driver: fullName,
  identifier: documentNumber,
  phone,
});

const toPayload = ({
  driver,
  identifier,
  phone,
}: DriverDraft): TransportDriverPayload => ({
  fullName: driver.trim(),
  documentNumber: identifier.trim(),
  phone: phone.trim(),
});

const DEBOUNCE_DELAY = 500;

export const useDriversDatabaseTable = () => {
  const { data, count, loading, getTransportDrivers } =
    useGetTransportDrivers();
  const { createTransportDriver, loading: creating } =
    useCreateTransportDriver();
  const { updateTransportDriver, loading: updating } =
    useUpdateTransportDriver();
  const { deleteTransportDriver } = useDeleteTransportDriver();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DriverDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDrivers = useCallback(
    () =>
      getTransportDrivers({
        filterText: searchValue,
        pageNumber: page + 1,
        resultSize: rowsPerPage,
      }),
    [getTransportDrivers, page, rowsPerPage, searchValue],
  );

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const debouncedSearch = useMemo(
    () => debounce(() => setPage(0), DEBOUNCE_DELAY),
    [],
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSearch();
  };

  const rows = useMemo(() => {
    const fetched = (data ?? []).map(toRow);
    return isAdding
      ? [{ id: NEW_ROW_ID, ...EMPTY_DRAFT }, ...fetched]
      : fetched;
  }, [data, isAdding]);

  const totalCount = count + (isAdding ? 1 : 0);
  const saving = creating || updating || deleting;

  const resetEditing = () => {
    setEditingId(null);
    setIsAdding(false);
    setDraft(EMPTY_DRAFT);
    setErrors(EMPTY_ERRORS);
  };

  const handleAdd = () => {
    setPage(0);
    setIsAdding(true);
    setEditingId(NEW_ROW_ID);
    setDraft(EMPTY_DRAFT);
    setErrors(EMPTY_ERRORS);
  };

  const handleStartEdit = ({ id, ...draftFields }: DriverRow) => {
    setEditingId(id);
    setDraft(draftFields);
    setErrors(EMPTY_ERRORS);
  };

  const handleDraftChange = (field: keyof DriverDraft, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({
      ...prev,
      [field]: value.length > MAX_LENGTHS[field],
    }));
  };

  const handleConfirmEdit = async (rowId: number) => {
    const nextErrors = {
      driver: isInvalid('driver', draft.driver),
      identifier: isInvalid('identifier', draft.identifier),
      phone: isInvalid('phone', draft.phone),
    };
    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }

    const isNew = rowId === NEW_ROW_ID;
    const errorMessage = isNew
      ? ERRORS.driverAddError
      : ERRORS.driverUpdateError;

    try {
      const payload = toPayload(draft);
      const response = isNew
        ? await createTransportDriver(payload)
        : await updateTransportDriver(rowId, payload);

      if (response?.status && isSuccessful(response)) {
        appToast.success(
          isNew
            ? DIALOGS.driverAddSuccessMessage
            : DIALOGS.driverUpdateSuccessMessage,
        );
        resetEditing();
        await fetchDrivers();
      }
    } catch (e) {
      if ((e as any)?.data?.message === 'TransportDriverIDAlreadyExists') {
        appToast.error(DIALOGS.driverAddErrorMessage);
      } else {
        appToast.error(errorMessage);
      }
    }
  };

  const handleCancelEdit = () => resetEditing();

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked
        ? rows.filter(({ id }) => id !== NEW_ROW_ID).map(({ id }) => id)
        : [],
    );

  const handleSelect = (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleDeleteConfirm = async () => {
    const deleteErrorMessage =
      selected.length > 1
        ? ERRORS.driverDeleteErrorMultiple
        : ERRORS.driverDeleteErrorSingle;

    setDeleting(true);
    try {
      const responses = await Promise.all(
        selected.map((id) => deleteTransportDriver(id)),
      );

      const allSuccessful = responses.every(
        (response) => response?.status && isSuccessful(response),
      );
      if (allSuccessful) {
        appToast.success(
          selected.length > 1
            ? DIALOGS.driverDeleteSuccessMessageMultiple
            : DIALOGS.driverDeleteSuccessMessageSingle,
        );
      } else {
        appToast.error(deleteErrorMessage);
      }

      if (editingId !== null && selected.includes(editingId)) resetEditing();

      const remaining = count - selected.length;
      const lastPage = Math.max(0, Math.ceil(remaining / rowsPerPage) - 1);

      setSelected([]);
      if (page > lastPage) setPage(lastPage);
      else await fetchDrivers();
    } catch {
      appToast.error(deleteErrorMessage);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setSelected([]);
    resetEditing();
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSelected([]);
    resetEditing();
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return {
    rows,
    totalCount,
    loading,
    saving,
    selected,
    page,
    rowsPerPage,
    searchValue,
    handleSearch,
    hoveredId,
    setHoveredId,
    editingId,
    draft,
    errors,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleAdd,
    handleStartEdit,
    handleDraftChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleSelectAll,
    handleSelect,
    handleDeleteConfirm,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
